import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentPersonalizationCompleteData,
  type EnrollmentPersonalizationCompleteSubmitInput,
  type EnrollmentPersonalizationCompleteSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment PersonalizationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPersonalizationCompleteData>
 * @example const response = await getEnrollmentPersonalizationCompleteData()
 */
export const getEnrollmentPersonalizationCompleteData = async (
  context?: DefaultContext,
): Promise<EnrollmentPersonalizationCompleteData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentPersonalizationCompleteData',
      query: `mutation getEnrollmentPersonalizationCompleteData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
                 userInfo {
                    employmentStatus
                    occupation
                    netWorthRange
                    income
                    investment {
                        riskTolerance {
                            type
                            allocation {
                                equityPercent
                                fixedIncomePercent
                                cashEquivalentPercent
                            }
                        }
                    }
                }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentPersonalizationCompleteData }
  return response.submitEnrollment
}

export const useGetEnrollmentPersonalizationCompleteData = createQueryHook<
  undefined,
  EnrollmentPersonalizationCompleteData
>(getEnrollmentPersonalizationCompleteData)

/**
 * Submit Enrollment PersonalizationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPersonalizationCompleteSubmitOutput>
 * @example const response = await submitEnrollmentPersonalizationCompleteData();
 */
export const submitEnrollmentPersonalizationCompleteData = async (
  enrollmentPersonalizationCompleteSubmitInput: EnrollmentPersonalizationCompleteSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentPersonalizationCompleteSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentPersonalizationCompleteData',
      query: `mutation submitEnrollmentPersonalizationCompleteData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentPersonalizationCompleteSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentPersonalizationCompleteSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentPersonalizationCompleteData = createMutationHook<
  EnrollmentPersonalizationCompleteSubmitInput,
  EnrollmentPersonalizationCompleteSubmitOutput
>(submitEnrollmentPersonalizationCompleteData)
